import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import type { Volume, VolumeAccent } from '../types/product';
const accentClasses: Record<VolumeAccent, {
  tag: string;
  text: string;
  soft: string;
}> = {
  blue: {
    tag: 'bg-blue-600',
    text: 'text-blue-700',
    soft: 'bg-blue-50'
  },
  teal: {
    tag: 'bg-teal-600',
    text: 'text-teal-700',
    soft: 'bg-teal-50'
  },
  green: {
    tag: 'bg-emerald-600',
    text: 'text-emerald-700',
    soft: 'bg-emerald-50'
  },
  purple: {
    tag: 'bg-violet-600',
    text: 'text-violet-700',
    soft: 'bg-violet-50'
  },
  orange: {
    tag: 'bg-orange-500',
    text: 'text-orange-700',
    soft: 'bg-orange-50'
  },
  red: {
    tag: 'bg-pulse',
    text: 'text-pulse-600',
    soft: 'bg-pulse-50'
  }
};
const PREVIEW_COUNT = 6;
type VolumeCardProps = {
  volume: Volume;
};
export function VolumeCard({
  volume
}: VolumeCardProps) {
  const [expanded, setExpanded] = useState(false);
  const accent = accentClasses[volume.accent];
  const shown = expanded ? volume.items : volume.items.slice(0, PREVIEW_COUNT);
  const hiddenCount = volume.items.length - PREVIEW_COUNT;
  return <article className="overflow-hidden rounded-3xl bg-white shadow-[0_20px_50px_-30px_rgba(6,18,42,0.4)] ring-1 ring-black/5" data-id="element-375" data-mp="3cau0ao70maum">
      <div className="grid md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" data-id="element-376" data-mp="2xl61btmb3oa2">
        <div className="bg-navy-900 p-6 text-white sm:p-8" data-id="element-377" data-mp="32ksc7wenwsfp">
          <span className={`inline-block rounded-lg px-3 py-1 font-display text-sm font-extrabold uppercase tracking-wider ${accent.tag}`} data-id="element-378" data-mp="32j7i27w9fbid">
            Volume {volume.numeral}
          </span>
          <h3 className="mt-4 font-display text-2xl font-black uppercase leading-tight sm:text-[28px]" data-id="element-379" data-mp="2tlx6smjfzmdg">{volume.title}</h3>
          <p className="mt-4 text-lg font-semibold leading-snug text-gold" data-id="element-380" data-mp="2eppjhdcqm4ty">{volume.headline}</p>
          <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-white/80" data-id="element-381" data-mp="2jo985liptuq0">
            {volume.intro.map(p => <p key={p} data-id="element-382" data-mp="hvl274eost6j">{p}</p>)}
          </div>
        </div>

        <div className="flex flex-col p-6 sm:p-8" data-id="element-383" data-mp="gdw76e8ueich">
          <h4 className={`font-display text-sm font-extrabold uppercase tracking-wider ${accent.text}`} data-id="element-384" data-mp="gep6iu5h25kl">
            Inside Volume {volume.numeral}
          </h4>
          <ul className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2" data-id="element-385" data-mp="6fgcy2eiqsyr">
            {shown.map(item => <li key={item} className="flex items-start gap-2.5 text-[15px] font-medium leading-snug text-ink" data-id="element-386" data-mp="2empfa4zng6e">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-heal" strokeWidth={3} aria-hidden="true" data-id="element-387" data-mp="z4nfrdl0a4pt" />
                {item}
              </li>)}
          </ul>
          {hiddenCount > 0 && <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className={`mt-4 inline-flex items-center gap-1.5 self-start rounded-lg py-1 text-sm font-bold ${accent.text} transition-opacity duration-150 hover:opacity-75`} data-id="element-388" data-mp="pd9i7bcm7ezb">
              {expanded ? 'Show fewer topics' : `See all ${volume.items.length} topics (+${hiddenCount})`}
              <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-389" data-mp="2vt9dwjd5oz3i" />
            </button>}

          <div className={`mt-6 rounded-2xl p-5 ${accent.soft} md:mt-auto`} data-id="element-390" data-mp="39qfrlzj0w7lv">
            <p className={`text-xs font-extrabold uppercase tracking-wider ${accent.text}`} data-id="element-391" data-mp="39objf5e3cmfs">Your takeaway</p>
            <p className="mt-1.5 text-[15px] font-semibold leading-relaxed text-ink" data-id="element-392" data-mp="3jovji4pvjkwf">{volume.takeaway}</p>
          </div>
          {volume.note && <p className="mt-4 text-xs leading-relaxed text-muted" data-id="element-393" data-mp="3ogn3txifsue4">{volume.note}</p>}
        </div>
      </div>
    </article>;
}